import { useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Send,
  MoreHorizontal,
  Reply,
  Trash2,
  CheckCircle,
  XCircle,
  Paperclip,
  FileText,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  useCommentsQuery,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  type Comment,
} from '@/hooks/useComments';
import { useAuth } from '@/hooks/useAuth';

interface Task {
  id: string;
  name: string;
}

interface TaskCommentsProps {
  task: Task;
}

export function TaskComments({ task }: TaskCommentsProps) {
  const { user: currentUser } = useAuth();
  const { data: comments, isLoading } = useCommentsQuery(task.id);
  const createComment = useCreateComment(task.id);
  const updateComment = useUpdateComment(task.id);
  const deleteComment = useDeleteComment(task.id);

  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(
    async (parentId?: string) => {
      const content = newComment.trim();
      if (!content) return;

      try {
        await createComment.mutateAsync({
          content,
          parentId,
        });
        setNewComment('');
        setReplyingTo(null);
      } catch (error) {
        console.error('Failed to post comment:', error);
      }
    },
    [newComment, createComment]
  );

  const handleReply = useCallback((commentId: string) => {
    setReplyingTo(commentId);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const handleToggleResolved = useCallback(
    (commentId: string, isResolved: boolean) => {
      updateComment.mutate({
        commentId,
        updates: { isResolved: !isResolved },
      });
    },
    [updateComment]
  );

  const handleDelete = useCallback(
    (commentId: string) => {
      setCommentToDelete(commentId);
      setDeleteDialogOpen(true);
    },
    []
  );

  const confirmDelete = useCallback(() => {
    if (commentToDelete) {
      deleteComment.mutate(commentToDelete);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  }, [commentToDelete, deleteComment]);

  // Organize comments into threads (top-level and replies)
  const topLevelComments = comments?.filter((c) => !c.parentId) || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
              <div className="h-16 bg-gray-100 animate-pulse rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* New Comment Input */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={currentUser?.profilePicture || undefined} />
            <AvatarFallback>
              {getInitials(currentUser?.firstName, currentUser?.lastName, currentUser?.email)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <Textarea
              ref={textareaRef}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={
                replyingTo ? 'Write a reply...' : 'Add a comment...'
              }
              className="min-h-[80px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(replyingTo || undefined);
                }
              }}
            />
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                {replyingTo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReplyingTo(null)}
                  >
                    Cancel Reply
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Ctrl/Cmd + Enter to send
                </span>
                <Button
                  size="sm"
                  onClick={() => handleSubmit(replyingTo || undefined)}
                  disabled={!newComment.trim() || createComment.isPending}
                >
                  <Send className="h-4 w-4 mr-1" />
                  {createComment.isPending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      {topLevelComments.length === 0 ? (
        <div className="py-8 text-center border rounded-lg border-dashed">
          <p className="text-sm text-muted-foreground">
            No comments yet. Start the conversation!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {topLevelComments.map((comment) => (
            <CommentThread
              key={comment.id}
              comment={comment}
              currentUserId={currentUser?.id}
              onReply={handleReply}
              onToggleResolved={handleToggleResolved}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface CommentThreadProps {
  comment: Comment;
  currentUserId?: string;
  onReply: (commentId: string) => void;
  onToggleResolved: (commentId: string, isResolved: boolean) => void;
  onDelete: (commentId: string) => void;
  depth?: number;
}

function CommentThread({
  comment,
  currentUserId,
  onReply,
  onToggleResolved,
  onDelete,
  depth = 0,
}: CommentThreadProps) {
  const isOwner = currentUserId === comment.userId;
  const maxDepth = 2; // Limit nesting depth

  const user = comment.user;
  const displayName = user
    ? user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email
    : 'Unknown User';

  return (
    <div className={depth > 0 ? 'ml-12 mt-3' : ''}>
      <div
        className={`flex gap-3 p-3 rounded-lg ${
          comment.isResolved ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
        }`}
      >
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={user?.profilePicture || undefined} />
          <AvatarFallback>
            {getInitials(user?.firstName, user?.lastName, user?.email)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{displayName}</span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.createdAt), {
                  addSuffix: true,
                })}
              </span>
              {comment.isResolved && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Resolved
                </span>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {depth < maxDepth && (
                  <DropdownMenuItem onClick={() => onReply(comment.id)}>
                    <Reply className="h-4 w-4 mr-2" />
                    Reply
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() =>
                    onToggleResolved(comment.id, comment.isResolved)
                  }
                >
                  {comment.isResolved ? (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Mark as Unresolved
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Resolved
                    </>
                  )}
                </DropdownMenuItem>
                {isOwner && (
                  <DropdownMenuItem
                    onClick={() => onDelete(comment.id)}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <p className="text-sm mt-1 whitespace-pre-wrap break-words">
            {comment.content}
          </p>

          {/* Attachments */}
          {comment.attachments && comment.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {comment.attachments.map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
                >
                  {attachment.mimeType.startsWith('image/') ? (
                    <Paperclip className="h-3 w-3" />
                  ) : (
                    <FileText className="h-3 w-3" />
                  )}
                  <span className="max-w-[150px] truncate">
                    {attachment.fileName}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-2">
          {comment.replies.map((reply) => (
            <CommentThread
              key={reply.id}
              comment={reply}
              currentUserId={currentUserId}
              onReply={onReply}
              onToggleResolved={onToggleResolved}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | undefined
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return '?';
}

export default TaskComments;
